import type { ReactNode } from "react";
import { useRef } from "react";

import debounce from "lodash/debounce";

import useIntersectionObserver from "../../hooks/useIntersectionObserver";

const MAIN_INTERSECTION_THRESHOLD = 0.75;
const DEFAULT_THRESHOLD = [0, MAIN_INTERSECTION_THRESHOLD, 1];
const ANIMATION_TIME = 200;

type SimpleStickySectionProps = {
    name: string;
    children: ReactNode;
    stickySectionRef?: React.RefObject<HTMLDivElement | null>;
    onIntersection?: (name: string) => void;
};

// TODO move to common-components: https://similarweb-rnd.atlassian.net/browse/SI-7584
export const SimpleStickySection = ({ name, children, stickySectionRef, onIntersection }: SimpleStickySectionProps) => {
    const prevRatio = useRef<number>(0);

    const handleIntersection = (entry: IntersectionObserverEntry) => {
        const { intersectionRatio, isIntersecting } = entry;

        if (
            isIntersecting &&
            intersectionRatio > prevRatio.current &&
            intersectionRatio >= MAIN_INTERSECTION_THRESHOLD
        ) {
            onIntersection?.(name);
        }

        prevRatio.current = intersectionRatio;
    };

    useIntersectionObserver({
        onIntersection: debounce(handleIntersection, ANIMATION_TIME),
        observingElement: stickySectionRef?.current,
        options: {
            threshold: DEFAULT_THRESHOLD,
            rootMargin: "-5% 0px -10% 0px",
        },
        isEnabled: !!onIntersection,
    });

    return (
        <div
            ref={stickySectionRef}
            data-automation="sticky-section"
            data-automation-section-name={name}
        >
            <div>{children}</div>
        </div>
    );
};
